(() => {
  'use strict';
  const controllers = new WeakMap();
  let active;

  function appAcknowledgement(form) {
    const property = form.querySelector('.bold_options [name="properties[Ship Time Acknowledgement]"]');
    return property?.matches('input[type="checkbox"]') ? property :
      property?.closest('.bold_option')?.querySelector('input[type="checkbox"]');
  }

  function acknowledgement(form) {
    return appAcknowledgement(form) || form.querySelector('[data-native-shipping-ack]:not(:disabled)');
  }

  function positionReminder() {
    if (!active || active.hand.hidden || !active.checkbox?.isConnected) return;
    const box = active.checkbox.getBoundingClientRect();
    // The glove's fingertip sits 22px from its left edge and 150px from its top.
    active.hand.style.left = `${Math.max(4, Math.min(innerWidth - 148, box.left + box.width / 2 - 22))}px`;
    active.hand.style.top = `${Math.max(4, box.top - 168)}px`;
  }

  function dismiss(controller) {
    controller.hand.hidden = true;
    controller.hand.classList.remove('is-pointing');
    controller.message.hidden = true;
    controller.checkbox?.removeAttribute('aria-invalid');
    controller.checkbox?.classList.remove('shipping-ack-attention');
    if (active === controller) active = null;
  }

  function sync(controller) {
    const { form, options, fallback } = controller;
    const appCheckbox = appAcknowledgement(form);
    const optionsReady = options.classList.contains('bold_options_loaded');
    if (fallback) {
      const useFallback = !appCheckbox && optionsReady;
      fallback.hidden = !useFallback;
      const input = fallback.querySelector('[data-native-shipping-ack]');
      if (input.disabled === useFallback) input.disabled = !useFallback;
    }
    const checkbox = acknowledgement(form);
    if (controller.checkbox && controller.checkbox !== checkbox) dismiss(controller);
    controller.checkbox = checkbox;
    const state = checkbox ? (checkbox.checked ? 'ready' : 'locked') : 'loading';
    if (form.dataset.shippingGate !== state) form.dataset.shippingGate = state;
    if (checkbox && !checkbox.required) checkbox.required = true;
    form.querySelectorAll('[data-add], [data-buy-now]').forEach(button => {
      const disabled = String(state !== 'ready' || button.disabled);
      if (button.getAttribute('aria-disabled') !== disabled) button.setAttribute('aria-disabled', disabled);
    });
    if (state === 'ready' && (!controller.hand.hidden || !controller.message.hidden)) dismiss(controller);
    if (active === controller) positionReminder();
    return state;
  }

  function remind(controller) {
    if (active && active !== controller) dismiss(active);
    const state = sync(controller);
    controller.message.textContent = state === 'loading'
      ? 'Your product options are loading. Please try again in a moment.'
      : 'Please check Ship Time Acknowledgement before adding this item to your cart.';
    controller.message.hidden = false;
    if (!controller.checkbox) return;
    active = controller;
    controller.checkbox.setAttribute('aria-invalid', 'true');
    controller.checkbox.classList.add('shipping-ack-attention');
    controller.checkbox.scrollIntoView({ block: 'center', behavior: 'auto' });
    controller.checkbox.focus({ preventScroll: true });
    controller.hand.hidden = false;
    controller.hand.classList.remove('is-pointing');
    positionReminder();
    // Restart a short reminder; the hand then stays still until acknowledged or dismissed.
    requestAnimationFrame(() => {
      if (active === controller) controller.hand.classList.add('is-pointing');
    });
  }

  function attach(form) {
    if (controllers.has(form)) return controllers.get(form);
    const options = form.querySelector('.bold_options');
    const hand = form.querySelector('[data-shipping-hand]');
    const message = form.querySelector('[data-shipping-message]');
    if (!options || !hand || !message) return null;
    const controller = { form, options, hand, message, fallback: form.querySelector('[data-shipping-fallback]'), checkbox: null };
    controllers.set(form, controller);
    let queued = false;
    new MutationObserver(() => {
      if (queued) return;
      queued = true;
      queueMicrotask(() => { queued = false; sync(controller); });
    }).observe(form, { childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'disabled', 'checked'] });
    form.addEventListener('change', () => sync(controller));
    sync(controller);
    return controller;
  }

  function controllerFor(target) {
    const form = target.closest?.('main .gga-product-form, .quick-buy-document .gga-product-form');
    return form ? attach(form) : null;
  }

  function blockUntilAcknowledged(event) {
    const controller = controllerFor(event.target);
    if (!controller) return;
    if (event.type === 'click' && !event.target.closest('[data-add], [data-buy-now]')) return;
    if (event.type === 'keydown' && (!['Enter', ' '].includes(event.key) || !event.target.closest('[data-add], [data-buy-now]'))) return;
    if (event.type === 'invalid' && event.target !== acknowledgement(controller.form)) return;
    if (sync(controller) === 'ready') return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (!event.repeat) remind(controller);
  }

  // Capture before the options app's button handler, including its cloned button.
  window.addEventListener('click', blockUntilAcknowledged, true);
  window.addEventListener('keydown', blockUntilAcknowledged, true);
  window.addEventListener('submit', blockUntilAcknowledged, true);
  window.addEventListener('invalid', blockUntilAcknowledged, true);
  window.addEventListener('keydown', event => { if (event.key === 'Escape' && active) dismiss(active); });
  window.addEventListener('scroll', positionReminder, { capture: true, passive: true });
  window.addEventListener('resize', positionReminder);
  window.addEventListener('pageshow', () => document.querySelectorAll('main .gga-product-form').forEach(form => { const c = attach(form); if (c) sync(c); }));
  document.addEventListener('shopify:section:load', () => document.querySelectorAll('main .gga-product-form').forEach(attach));
  document.addEventListener('gga:product-ready', event => { if (event.detail?.form) attach(event.detail.form); });
  document.querySelectorAll('main .gga-product-form').forEach(attach);
})();
